import type { RouteObject } from 'react-router-dom';
import type { TelemetryRouteHandle } from '@agnivo/app-core';

/**
 * The section's route objects — this module is EAGER (it is only data: paths and
 * lazy thunks), so importing it costs nothing in web-ui's main bundle. Every
 * screen-shaped import is a dynamic `import()` below, and they all target the
 * same entry module: one lazy chunk for the whole section
 * (architect constraint 1, 2026-08-18T15:44Z).
 *
 * `lazy` is a DATA-ROUTER feature (createBrowserRouter/createMemoryRouter) — it
 * does not resolve under declarative <Routes>. web-ui mounts this table through
 * createBrowserRouter; the agentSwitch test reaches the keyed detail screen via
 * the entry module's export instead (see test/admin-tenant/).
 *
 * Paths are ABSOLUTE ('/agents', not 'agents'): these children mount directly
 * under web-ui's shared Layout root via replaceRoutesByPath (Dmitry DM 452:
 * the section owns /agents; chat-side /agents/new stays mounted until the
 * section's own create flow lands — architect staging 2026-08-18T16:08Z).
 *
 * `handle.telemetryRoute` is the render-failure producer's route attribution
 * (F7, web!515 round 4): the section's detail screen is where `agent-detail`
 * is claimed on THIS surface — the chat-side table has no such route, so the
 * token would be unreachable if it were not declared here, on the definition
 * that actually renders.
 */
export const sectionRoutes: RouteObject[] = [
  {
    path: '/agents',
    // `ensureAdminMockRegistered()` awaited HERE, not fired-and-forgotten: `lazy()`'s own promise
    // is what React Router refuses to resolve `{ Component }` before, which is what makes the
    // section's mock registration a structural precondition of rendering — see `entry.tsx`'s
    // docblock on `ensureAdminMockRegistered`.
    lazy: () => import('./entry').then((m) => m.ensureAdminMockRegistered().then(() => ({ Component: m.SectionList }))),
    handle: { telemetryRoute: 'agents' } satisfies TelemetryRouteHandle,
  },
  {
    path: '/agents/:agentId',
    lazy: () => import('./entry').then((m) => m.ensureAdminMockRegistered().then(() => ({ Component: m.SectionDetail }))),
    handle: { telemetryRoute: 'agent-detail' } satisfies TelemetryRouteHandle,
  },
];

/**
 * ADR-156 widget-config page — DEV ONLY, flag-gated (`appConfig.widgetConfigEnabled`; `App.tsx`
 * spreads this table in ONLY when the flag is on, so with it off the path does not exist).
 *
 * Product decision `decisions/product/2026-10-05-widget-config-page-remount-dev-behind-flag.md`:
 * the intended home is `/agents/:agentId/widgets`, entered from the agent page. The screen lists
 * EVERY installation with an agent picker and cannot filter by agent without a package change, so
 * this is the permitted fallback — a flag-gated top-level `/widgets`, dev only. It has no nav
 * entry (reachable by URL). The per-agent placement is the follow-up.
 *
 * Its OWN lazy chunk (`./widgetsEntry`), deliberately NOT `./entry`: the section's single-chunk
 * rule (architect constraint 1, above) exists to keep one chunk, and folding this screen into
 * `entry` would grow `/agents`' payload against the 195,000-byte `total-byte-weight` budget for a
 * dev-only page. NO `telemetryRoute` handle: `widgets` is not a `ClientEventRoute` token (the contract
 * is a closed vocabulary — adding one is a proto-first change), so a render failure here reports
 * unattributed rather than under a token the contract would refuse. Mock registration is shared (`ensureAdminMockRegistered`, which already composes
 * the widget module).
 */
export const widgetRoutes: RouteObject[] = [
  {
    path: '/widgets',
    // Registration is awaited INSIDE `loadWidgets` (same guarantee as the section routes above); the
    // call lives in the lazy chunk so none of it is paid in the eager `/agents` payload.
    lazy: () => import('./widgetsEntry').then((w) => w.loadWidgets()),
  },
];

/**
 * THE gate, as a function so both arms are testable through the code `App.tsx` actually runs
 * (the call site is source-pinned in `test/accountRoutePin.test.ts`; both arms and the unset
 * default are exercised in `test/adminSectionComposition.test.ts`).
 */
export function widgetRoutesFor(widgetConfigEnabled: boolean | undefined): RouteObject[] {
  return widgetConfigEnabled === true ? widgetRoutes : [];
}
